(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(root);
  } else {
    root.HLA_STYLES_THAT_PAIR = factory(root);
    root.HLA_STYLES_THAT_PAIR.initAll();
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  const API_BASE = 'https://erp.haydenla.com/api/outfit-pairings';
  const DEFAULT_LIMIT = 6;
  const DEFAULT_TIMEOUT_MS = 6000;
  const PRODUCT_TIMEOUT_MS = 6000;
  const SLOT_LABELS = {
    bottom: 'Bottom',
    top: 'Top',
    outerwear: 'Outerwear',
    dress: 'Dress',
    set: 'Set',
  };
  const SIZE_ORDER = [
    'XXS',
    'XS',
    'S',
    'M',
    'L',
    'XL',
    'XXL',
    '1X',
    '2X',
    '3X',
    '4X',
    '5X',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '10',
    '12',
    '14',
    '16',
  ];
  const SIZE_ALIASES = new Map(SIZE_ORDER.map(size => [size.toLowerCase(), size]));

  function clampLimit(value) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_LIMIT;
    return Math.min(DEFAULT_LIMIT, Math.max(1, Math.floor(parsed)));
  }

  function hideResult(reason) {
    return { visible: false, reason, cards: [] };
  }

  function normalizeString(value) {
    return String(value || '').trim();
  }

  function normalizeHandle(value) {
    return normalizeString(value).toLowerCase();
  }

  function normalizeReasons(value) {
    if (!Array.isArray(value)) return [];
    return value.map(normalizeString).filter(Boolean).slice(0, 2);
  }

  function normalizeTags(value) {
    if (!Array.isArray(value)) return [];
    return value.map(normalizeString).filter(Boolean);
  }

  function normalizePairResponse(data, options = {}) {
    if (!data || data.ok !== true || !Array.isArray(data.pairs)) {
      return hideResult('bad-response');
    }

    const limit = clampLimit(options.limit);
    const sourceHandle = normalizeHandle(data.source && data.source.handle);
    const seen = new Set();
    const cards = [];

    data.pairs.forEach((pair, index) => {
      if (cards.length >= limit || !pair || typeof pair !== 'object') return;
      const handle = normalizeHandle(pair.handle);
      if (!handle || handle === sourceHandle || seen.has(handle)) return;
      seen.add(handle);

      const slot = normalizeHandle(pair.slot);
      cards.push({
        index,
        handle,
        title: normalizeString(pair.title),
        url: normalizeString(pair.url) || `/products/${handle}`,
        slot,
        slotLabel: SLOT_LABELS[slot] || 'Pairing',
        score: Number.isFinite(Number(pair.score)) ? Number(pair.score) : null,
        reasons: normalizeReasons(pair.reasons),
        occasionTags: normalizeTags(pair.occasion_tags),
        isCounterpart: pair.is_counterpart === true,
      });
    });

    if (!cards.length) return hideResult('empty');
    return { visible: true, reason: 'ok', cards };
  }

  function errorToRenderState() {
    return hideResult('error');
  }

  function buildPairApiUrl(handle, limit = DEFAULT_LIMIT) {
    const url = new URL(API_BASE);
    url.searchParams.set('store', 'ws');
    url.searchParams.set('handle', handle);
    url.searchParams.set('limit', String(clampLimit(limit)));
    return url.toString();
  }

  function buildProductJsonUrl(handle) {
    return `/products/${encodeURIComponent(handle)}.js`;
  }

  function buildAvailabilitySectionUrl(handle) {
    return `/products/${encodeURIComponent(handle)}?section_id=hla-product-availability-json`;
  }

  function withVariantUrl(url, variantId) {
    const source = normalizeString(url);
    const id = normalizeString(variantId);
    if (!source || !id) return source;
    if (/([?&])variant=[^&#]*/i.test(source)) {
      return source.replace(/([?&])variant=[^&#]*/i, `$1variant=${encodeURIComponent(id)}`);
    }
    const hashIndex = source.indexOf('#');
    const beforeHash = hashIndex >= 0 ? source.slice(0, hashIndex) : source;
    const hash = hashIndex >= 0 ? source.slice(hashIndex) : '';
    return `${beforeHash}${beforeHash.includes('?') ? '&' : '?'}variant=${encodeURIComponent(id)}${hash}`;
  }

  function fetchWithTimeout(url, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    const requestOptions = { ...options };
    if (controller) requestOptions.signal = controller.signal;

    return fetch(url, requestOptions).finally(() => {
      if (timer) clearTimeout(timer);
    });
  }

  async function fetchJsonWithTimeout(url, timeoutMs, label = 'json') {
    const response = await fetchWithTimeout(url, {
      credentials: url.startsWith('/products/') ? 'same-origin' : 'omit',
      headers: { Accept: 'application/json' },
    }, timeoutMs);
    if (!response.ok) throw new Error(`${label}-${response.status}`);
    return response.json();
  }

  async function fetchTextWithTimeout(url, timeoutMs, label = 'text') {
    const response = await fetchWithTimeout(url, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    }, timeoutMs);
    if (!response.ok) throw new Error(`${label}-${response.status}`);
    return response.text();
  }

  function parseVariantAvailabilityHtml(html) {
    const source = String(html || '');
    const match = source.match(/<script[^>]*data-hla-variant-availability-json[^>]*>([\s\S]*?)<\/script>/i);
    if (!match) return null;
    try {
      const parsed = JSON.parse(match[1]);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
      return null;
    }
  }

  function availabilityEntryForVariant(payload, variant) {
    const variants = payload && payload.variants;
    if (!variants || typeof variants !== 'object' || !variant) return null;
    const id = normalizeString(variant.id).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    return variants[id] || variants[normalizeString(variant.id)] || null;
  }

  function makeNode(doc, tagName, className, text) {
    const node = doc.createElement(tagName);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function normalizeImage(url) {
    const value = normalizeString(url);
    if (!value) return '';
    if (value.startsWith('//')) {
      const protocol = root.location && root.location.protocol ? root.location.protocol : 'https:';
      return `${protocol}${value}`;
    }
    return value;
  }

  function productTags(product) {
    if (Array.isArray(product && product.tags)) return product.tags.map(normalizeString).filter(Boolean);
    return [];
  }

  function hasTag(product, expected) {
    const target = normalizeString(expected).toLowerCase();
    return productTags(product).some(tag => tag.toLowerCase() === target);
  }

  function selectProductVariant(product, availabilityPayload = null) {
    const variants = Array.isArray(product && product.variants) ? product.variants : [];
    if (!variants.length) return {};
    const visibleVariants = variants.filter(variant => availabilityEntryForVariant(availabilityPayload, variant)?.hidden !== true);
    if (!visibleVariants.length) return {};
    const isPackVariant = variant => {
      const text = [
        variant.title,
        variant.public_title,
        variant.option1,
        variant.option2,
        variant.option3,
        ...(Array.isArray(variant.options) ? variant.options : []),
      ].map(normalizeString).join(' ');
      return /\bpack\b/i.test(text);
    };
    const orderable = variant => {
      const entry = availabilityEntryForVariant(availabilityPayload, variant);
      return typeof entry?.orderable === 'boolean' ? entry.orderable : variant.available;
    };
    return visibleVariants.find(variant => orderable(variant) && isPackVariant(variant))
      || visibleVariants.find(isPackVariant)
      || visibleVariants.find(orderable)
      || visibleVariants[0];
  }

  function imageForProduct(product, variant) {
    return normalizeImage(
      (variant && variant.featured_image && variant.featured_image.src)
      || (variant && variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src)
      || (product && product.featured_image)
      || (Array.isArray(product && product.images) ? product.images[0] : '')
    );
  }

  function explicitPackSize(product, variant) {
    const candidates = [
      product && product.pack_size,
      product && product.packSize,
      product && product.metafields && product.metafields.global && product.metafields.global.pack_size,
      product && product.metafields && product.metafields.global && product.metafields.global.pack_size && product.metafields.global.pack_size.value,
      variant && variant.pack_size,
      variant && variant.packSize,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) return Math.round(parsed);
    }

    const searchable = [
      ...productTags(product),
      variant && variant.title,
      variant && variant.public_title,
      variant && variant.sku,
    ].map(normalizeString);
    for (const value of searchable) {
      const match = value.match(/\b(?:pack\s*(?:of)?|case\s*(?:of)?)\s*(\d{1,2})\b/i)
        || value.match(/\b(\d{1,2})\s*(?:pc|pcs|piece|pieces)\b/i)
        || value.match(/\b(\d{1,2})\s*pack\b/i);
      if (match) {
        const parsed = Number(match[1]);
        if (Number.isFinite(parsed) && parsed > 0) return Math.round(parsed);
      }
    }

    return 0;
  }

  function deriveSizeRatio(product) {
    const tagSet = new Set(productTags(product).map(tag => tag.toLowerCase()));
    const sizes = SIZE_ORDER.filter(size => tagSet.has(size.toLowerCase()));
    if (sizes.length) return sizes.join('-');

    for (const tag of productTags(product)) {
      const normalized = tag.replace(/\s+/g, '').replace(/\//g, '-');
      const parts = normalized.split('-').filter(Boolean);
      if (parts.length < 2) continue;
      const labels = parts.map(part => SIZE_ALIASES.get(part.toLowerCase()));
      if (labels.every(Boolean)) return labels.join('-');
    }

    return '';
  }

  function derivePackSize(product, variant, sizeRatio) {
    const explicit = explicitPackSize(product, variant);
    if (explicit > 0) return explicit;
    const sizes = sizeRatio ? sizeRatio.split('-').filter(Boolean) : [];
    if (sizes.length > 0) return sizes.length * 2;
    return /\bpack\b/i.test([variant && variant.title, variant && variant.public_title, variant && variant.sku].map(normalizeString).join(' '))
      ? 6
      : 1;
  }

  function formatMoney(cents) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';
    return `$${(value / 100).toFixed(2)}`;
  }

  function availabilityForProduct(product, variant, availabilityPayload = null) {
    const entry = availabilityEntryForVariant(availabilityPayload, variant);
    if (entry?.hidden === true) return '';
    if (normalizeString(entry?.label)) return normalizeString(entry.label);
    const variantMetafield = variant
      && variant.metafields
      && variant.metafields.custom
      && variant.metafields.custom.availability_status;
    const variantStatus = normalizeString(variantMetafield && (variantMetafield.value || variantMetafield));
    if (variantStatus) return variantStatus === 'Out of Stock' ? 'Sold Out' : variantStatus;
    if (hasTag(product, 'Out of Stock')) return 'Sold Out';
    if (hasTag(product, 'Incoming')) return 'Incoming';
    if (hasTag(product, 'In Stock')) return 'In Stock';
    if ((variant && variant.available) || (product && product.available)) return 'In Stock';
    return 'Sold Out';
  }

  function productCardData(product, pair = {}, availabilityPayload = null) {
    if (!product || typeof product !== 'object') return null;
    const variant = selectProductVariant(product, availabilityPayload);
    const variantId = normalizeString(variant && variant.id);
    if (!variantId) return null;

    const sizeRatio = deriveSizeRatio(product);
    const packSize = derivePackSize(product, variant, sizeRatio);
    const packCents = Number(variant && variant.price) || Number(product.price) || 0;
    const unitCents = packSize > 0 ? Math.round(packCents / packSize) : packCents;
    const handle = normalizeHandle(product.handle || pair.handle);
    const productUrl = normalizeString(pair.url) || normalizeString(product.url) || (handle ? `/products/${handle}` : '#');
    const availabilityEntry = availabilityEntryForVariant(availabilityPayload, variant);
    const availability = availabilityForProduct(product, variant, availabilityPayload);
    if (!availability) return null;
    const orderable = typeof availabilityEntry?.orderable === 'boolean'
      ? availabilityEntry.orderable
      : availability !== 'Sold Out' && variant.available !== false && product.available !== false;

    return {
      productId: normalizeString(product.id),
      handle,
      title: normalizeString(product.title || pair.title),
      url: withVariantUrl(productUrl, variantId),
      variantId,
      sku: normalizeString(variant && variant.sku),
      image: imageForProduct(product, variant),
      unitPrice: `${formatMoney(unitCents)}/unit`,
      packPrice: formatMoney(packCents),
      unitCents,
      packCents,
      packSize: packSize > 0 ? String(packSize) : '',
      sizeRatio,
      packLine: [packSize > 0 ? `${packSize} pcs` : '', sizeRatio].filter(Boolean).join(' '),
      availability,
      available: orderable,
      availabilityState: normalizeString(availabilityEntry?.policyState || availabilityEntry?.visualState),
    };
  }

  function setDataset(node, values) {
    Object.entries(values).forEach(([key, value]) => {
      if (value == null || value === '') return;
      node.dataset[key] = String(value);
    });
  }

  function injectPairBadges(card, pair, doc) {
    const badgeRow = makeNode(doc, 'div', 'shop-the-look__badges');
    badgeRow.appendChild(makeNode(doc, 'span', 'shop-the-look__slot', pair.slotLabel));
    if (pair.isCounterpart) {
      badgeRow.appendChild(makeNode(doc, 'span', 'shop-the-look__matching-set', 'Matching set'));
    }
    const target = card.querySelector('.card__inner') || card;
    target.insertBefore(badgeRow, target.firstChild);
  }

  function buildActions(doc, title) {
    const actions = makeNode(doc, 'div', 'hla-card-actions no-js-hidden');
    actions.setAttribute('aria-label', 'Product actions');

    const favorite = makeNode(doc, 'button', 'hla-card-action hla-card-action--favorite');
    favorite.type = 'button';
    favorite.dataset.hlaFavoriteToggle = '';
    favorite.setAttribute('aria-pressed', 'false');
    favorite.setAttribute('aria-label', `Save ${title}`.trim());
    favorite.appendChild(makeNode(doc, 'span', 'hla-card-action__heart'));
    favorite.firstChild.setAttribute('aria-hidden', 'true');
    actions.appendChild(favorite);

    const quickView = makeNode(doc, 'button', 'hla-card-action hla-card-action--quick-view', 'Quick View');
    quickView.type = 'button';
    quickView.dataset.hlaQuickView = '';
    quickView.setAttribute('aria-label', `Quick View ${title}`.trim());
    actions.appendChild(quickView);

    return actions;
  }

  function buildCardMedia(doc, data) {
    const inner = makeNode(doc, 'div', `card__inner${data.image ? ' ratio' : ''}`);
    if (data.image) {
      inner.setAttribute('style', '--ratio-percent: 125%;');
      const mediaWrap = makeNode(doc, 'div', 'card__media');
      const media = makeNode(doc, 'div', 'media media--transparent media--hover-effect');
      const link = makeNode(doc, 'a', 'full-unstyled-link');
      link.href = data.url;
      const image = makeNode(doc, 'img', 'motion-reduce');
      image.src = data.image;
      image.alt = data.title;
      image.loading = 'lazy';
      image.width = 533;
      image.height = 666;
      link.appendChild(image);
      media.appendChild(link);
      mediaWrap.appendChild(media);
      inner.appendChild(mediaWrap);
    }
    inner.appendChild(buildActions(doc, data.title));
    return inner;
  }

  function availabilityClass(availability) {
    const value = normalizeString(availability).toLowerCase();
    if (value === 'in stock') return 'card__availability card__availability--in-stock';
    if (value === 'incoming' || value.startsWith('eta ')) return 'card__availability card__availability--incoming';
    if (value && value !== 'sold out') return 'card__availability card__availability--preorder';
    return 'card__availability card__availability--sold-out';
  }

  function buildPriceBlock(doc, data) {
    const price = makeNode(doc, 'div', 'wholesale-price shop-the-look__price');
    price.appendChild(makeNode(doc, 'span', 'wholesale-price__unit', data.unitPrice));

    const pack = makeNode(doc, 'div', 'shop-the-look__pack-price');
    pack.appendChild(makeNode(doc, 'span', '', 'Pack'));
    pack.appendChild(makeNode(doc, 'strong', '', data.packLine || data.packPrice));

    const wrapper = makeNode(doc, 'div', 'shop-the-look__price-wrap');
    wrapper.appendChild(price);
    wrapper.appendChild(pack);
    return wrapper;
  }

  function buildCardInfo(doc, data, pair) {
    const content = makeNode(doc, 'div', 'card__content');
    const info = makeNode(doc, 'div', 'card__information');

    const heading = makeNode(doc, 'h3', 'card__heading');
    const link = makeNode(doc, 'a', 'full-unstyled-link', data.title);
    link.href = data.url;
    heading.appendChild(link);
    info.appendChild(heading);

    const details = makeNode(doc, 'div', 'card-information');
    details.appendChild(buildPriceBlock(doc, data));
    details.appendChild(makeNode(doc, 'span', availabilityClass(data.availability), data.availability));
    info.appendChild(details);
    content.appendChild(info);
    return content;
  }

  function injectCartAction(card, doc) {
    const action = makeNode(doc, 'div', 'shop-the-look__cart-action');
    const button = makeNode(doc, 'button', 'button button--secondary shop-the-look__add-pack', 'Add to Cart');
    button.type = 'button';
    button.dataset.stylesThatPairAdd = '';
    if (!isCardAddable(card)) {
      button.disabled = true;
      button.textContent = 'Unavailable';
    }
    action.appendChild(button);
    const content = card.querySelector('.card__information') || card.querySelector('.card__content') || card;
    content.appendChild(action);
  }

  function buildProductCard(product, pair, doc = root.document, availabilityPayload = null) {
    if (!doc) return null;
    const data = productCardData(product, pair, availabilityPayload);
    if (!data) return null;

    const card = makeNode(doc, 'div', 'card-wrapper product-card-wrapper underline-links-hover shop-the-look__card');
    card.dataset.hlaProductCard = '';
    setDataset(card, {
      productId: data.productId,
      productHandle: data.handle,
      productTitle: data.title,
      productUrl: data.url,
      variantId: data.variantId,
      sku: data.sku,
      unitPrice: data.unitPrice,
      packPrice: data.packPrice,
      unitCents: data.unitCents,
      packCents: data.packCents,
      packSize: data.packSize,
      sizeRatio: data.sizeRatio,
      availability: data.availability,
      availabilityState: data.availabilityState,
      cardImage: data.image,
      pairHandle: pair.handle,
      pairSlot: pair.slot,
      pairIndex: pair.index,
      pairCardAvailable: data.available ? 'true' : 'false',
      pairCardVariantId: data.variantId,
    });

    const cardBody = makeNode(doc, 'div', `card${data.image ? ' card--media' : ' card--text'}`);
    cardBody.appendChild(buildCardMedia(doc, data));
    cardBody.appendChild(buildCardInfo(doc, data, pair));
    card.appendChild(cardBody);

    injectPairBadges(card, pair, doc);
    injectCartAction(card, doc);
    return card;
  }

  function isCardAddable(card) {
    const variantId = normalizeString(card.dataset.variantId || card.dataset.pairCardVariantId);
    if (!variantId) return false;
    if (card.dataset.pairCardAvailable === 'false') return false;
    if (normalizeString(card.dataset.availability).toLowerCase() === 'sold out') return false;
    return true;
  }

  function setButtonState(button, state, text) {
    if (!button) return;
    button.dataset.state = state;
    button.textContent = text;
  }

  function findCartDrawer() {
    const doc = root.document;
    return doc ? doc.querySelector('cart-drawer') : null;
  }

  function cartSectionIds(cartDrawer) {
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      const ids = cartDrawer.getSectionsToRender().map(section => section && section.id).filter(Boolean);
      if (ids.length) return ids;
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  // Mirror the theme's native add-to-cart: request the cart sections in the
  // add.js call and hand them to <cart-drawer> so the header count and drawer
  // update (and the drawer opens) without a page refresh.
  function refreshCartDrawer(cartDrawer, parsed) {
    if (!cartDrawer || !parsed || !parsed.sections || typeof cartDrawer.renderContents !== 'function') {
      return false;
    }
    try {
      cartDrawer.renderContents(parsed);
      return true;
    } catch (_) {
      return false;
    }
  }

  async function addItemsToCart(items, button) {
    if (!items.length) return;
    const originalText = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      setButtonState(button, 'loading', 'Adding...');
    }
    try {
      const cartDrawer = findCartDrawer();
      const body = { items };
      if (cartDrawer) {
        body.sections = cartSectionIds(cartDrawer);
        body.sections_url = root.location ? root.location.pathname : '/';
      }
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(`cart-add-${response.status}`);
      const parsed = await response.json().catch(() => null);
      refreshCartDrawer(cartDrawer, parsed);
      if (button) {
        setButtonState(button, 'added', items.length > 1 ? 'Look added' : 'Added');
        setTimeout(() => {
          button.disabled = false;
          setButtonState(button, 'idle', originalText || 'Add to Cart');
        }, 1800);
      }
    } catch (_) {
      if (button) {
        setButtonState(button, 'error', 'Try again');
        setTimeout(() => {
          button.disabled = false;
          setButtonState(button, 'idle', originalText || 'Add to Cart');
        }, 1800);
      }
    }
  }

  function cardAddItems(cards) {
    return cards
      .filter(isCardAddable)
      .map(card => ({ id: Number(card.dataset.variantId || card.dataset.pairCardVariantId), quantity: 1 }))
      .filter(item => Number.isFinite(item.id) && item.id > 0);
  }

  function refreshCardActions() {
    const actions = root.HLA_CARD_ACTIONS;
    if (actions && typeof actions.refresh === 'function') {
      actions.refresh();
    } else if (actions && typeof actions.updateFavoriteButtons === 'function') {
      actions.updateFavoriteButtons();
    }
  }

  async function fetchProductCard(pair, options) {
    const timeoutMs = options.productTimeoutMs || PRODUCT_TIMEOUT_MS;
    const [product, availabilityPayload] = await Promise.all([
      fetchJsonWithTimeout(buildProductJsonUrl(pair.handle), timeoutMs, 'product-json'),
      fetchTextWithTimeout(buildAvailabilitySectionUrl(pair.handle), timeoutMs, 'availability-section')
        .then(parseVariantAvailabilityHtml)
        .catch(() => null),
    ]);
    return buildProductCard(product, pair, options.doc || root.document, availabilityPayload);
  }

  async function renderCards(section, pairs, options) {
    const grid = section.querySelector('[data-styles-that-pair-grid]');
    if (!grid) return [];
    const renderOptions = {
      ...options,
      doc: section.ownerDocument || root.document,
    };
    const settled = await Promise.allSettled(pairs.map(pair => fetchProductCard(pair, renderOptions)));
    const cards = settled
      .filter(result => result.status === 'fulfilled' && result.value)
      .map(result => result.value);
    if (!cards.length) return [];

    grid.replaceChildren(...cards);
    refreshCardActions();
    return cards;
  }

  function hideSection(section, reason) {
    section.hidden = true;
    section.setAttribute('aria-busy', 'false');
    section.dataset.stylesThatPairState = reason || 'hidden';
  }

  function showSection(section, cards) {
    const addAll = section.querySelector('[data-styles-that-pair-add-all]');
    const items = cardAddItems(cards);
    if (addAll && items.length > 1) {
      addAll.hidden = false;
      addAll.onclick = () => addItemsToCart(items, addAll);
    }

    section.dataset.stylesThatPairState = 'shown';
    section.setAttribute('aria-busy', 'false');
    section.hidden = false;
  }

  function bindCardCartActions(section) {
    section.addEventListener('click', event => {
      const button = event.target.closest('[data-styles-that-pair-add]');
      if (!button) return;
      const card = button.closest('[data-hla-product-card]');
      if (!card || !isCardAddable(card)) return;
      const items = cardAddItems([card]);
      if (!items.length) return;
      event.preventDefault();
      addItemsToCart(items, button);
    });
  }

  async function initSection(section) {
    if (!section || section.dataset.stylesThatPairInit === 'true') return;
    section.dataset.stylesThatPairInit = 'true';
    bindCardCartActions(section);

    const handle = normalizeHandle(section.dataset.productHandle);
    if (!handle) {
      hideSection(section, 'missing-handle');
      return;
    }

    const limit = clampLimit(section.dataset.limit);
    const timeoutMs = Number(section.dataset.timeoutMs) || DEFAULT_TIMEOUT_MS;
    const options = {
      productTimeoutMs: PRODUCT_TIMEOUT_MS,
    };

    try {
      const data = await fetchJsonWithTimeout(buildPairApiUrl(handle, limit), timeoutMs, 'pairing-api');
      const state = normalizePairResponse(data, { limit });
      if (!state.visible) {
        hideSection(section, state.reason);
        return;
      }
      const cards = await renderCards(section, state.cards, options);
      if (!cards.length) {
        hideSection(section, 'no-rendered-cards');
        return;
      }
      showSection(section, cards);
    } catch (_) {
      const state = errorToRenderState();
      hideSection(section, state.reason);
    }
  }

  function initAll(doc = root.document) {
    if (!doc) return;
    doc.querySelectorAll('[data-styles-that-pair-section]').forEach(initSection);
  }

  return {
    buildPairApiUrl,
    buildAvailabilitySectionUrl,
    buildProductCard,
    buildProductJsonUrl,
    addItemsToCart,
    cardAddItems,
    errorToRenderState,
    initAll,
    initSection,
    normalizePairResponse,
    parseVariantAvailabilityHtml,
    productCardData,
  };
});
